/**
 * Which Actions-table columns are on screen.
 *
 * The table used to drop Briefed POC, Start and Due outright whenever the map
 * opened, so those fields vanished exactly when someone was working spatially
 * and had no way to ask for them back. Narrow mode is now only a _default_: the
 * first time the user opens the column menu their choice is stored and narrow
 * mode stops overriding it.
 */

import type { VisibilityState } from '@tanstack/react-table';
import type { IapScope } from '../ICSConstants';

export const ACTION_COLUMN_IDS = [
  'display_id',
  'name',
  'subtask_completion',
  'description',
  'aor',
  'workspace',
  'assignee',
  'priority',
  'classification',
  'briefed_poc',
  'status',
  'start_date',
  'target_date',
  'blocking',
  'location',
] as const;

export type ActionColumnId = (typeof ACTION_COLUMN_IDS)[number];

export const ACTION_COLUMN_LABELS: Record<ActionColumnId, string> = {
  display_id: 'ID',
  name: 'Task',
  subtask_completion: 'Sub-Task Completion',
  description: 'Description',
  aor: 'AOR',
  workspace: 'Workspace',
  assignee: 'Assignee',
  priority: 'Priority',
  classification: 'Classification',
  briefed_poc: 'Briefed POC',
  status: 'Status',
  start_date: 'Start',
  target_date: 'Due',
  blocking: 'Blocking',
  location: 'Location',
};

/** ID and Task always show so a row stays identifiable. */
export const REQUIRED_ACTION_COLUMNS: readonly ActionColumnId[] = [
  'display_id',
  'name',
];

/** Incident-only columns, absent from the menu in org scope. */
const INCIDENT_ONLY_COLUMNS: readonly ActionColumnId[] = [
  'workspace',
  'classification',
];

/** Hidden by default when the map takes half the width. */
const NARROW_HIDDEN_COLUMNS: readonly ActionColumnId[] = [
  'description',
  'aor',
  'workspace',
  'briefed_poc',
  'start_date',
  'location',
];

export function actionColumnsForScope(scope: IapScope): ActionColumnId[] {
  return ACTION_COLUMN_IDS.filter(
    (id) => scope === 'incident' || !INCIDENT_ONLY_COLUMNS.includes(id)
  );
}

/**
 * TanStack's visibility map. Only the hidden columns need an entry, but listing
 * every column keeps the menu's checkbox state readable straight off the
 * table.
 */
export function actionColumnVisibility(
  scope: IapScope,
  hidden: readonly string[]
): VisibilityState {
  const visibility: VisibilityState = {};
  actionColumnsForScope(scope).forEach((id) => {
    visibility[id] =
      !hidden.includes(id) || REQUIRED_ACTION_COLUMNS.includes(id);
  });
  return visibility;
}

export function defaultHiddenActionColumns(narrow: boolean): ActionColumnId[] {
  return narrow ? [...NARROW_HIDDEN_COLUMNS] : [];
}

/** Shown by default on a roster position's Planning and Execution tables. */
const ROSTER_POSITION_VISIBLE_COLUMNS: readonly ActionColumnId[] = [
  'display_id',
  'name',
  'subtask_completion',
  'status',
];

/** Every other incident column stays in the menu and starts hidden. */
export function rosterPositionHiddenActionColumns(): ActionColumnId[] {
  return actionColumnsForScope('incident').filter(
    (id) => !ROSTER_POSITION_VISIBLE_COLUMNS.includes(id)
  );
}

export function rosterPositionColumnStorageKey(): string {
  return `${actionColumnStorageKey('incident')}:roster-position`;
}

export function toggleActionColumn(
  hidden: readonly string[],
  id: ActionColumnId
): ActionColumnId[] {
  if (REQUIRED_ACTION_COLUMNS.includes(id)) return hidden as ActionColumnId[];
  return (
    hidden.includes(id)
      ? hidden.filter((existing) => existing !== id)
      : [...hidden, id]
  ) as ActionColumnId[];
}

export function actionColumnStorageKey(scope: IapScope): string {
  return `actions-table-hidden-columns:v2:${scope}`;
}

/**
 * A stored preference, or null when the user has never expressed one. Anything
 * unrecognised is discarded rather than trusted, since the list of columns
 * changes as the table does.
 */
export function parseHiddenActionColumns(
  raw: string | null
): ActionColumnId[] | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.filter((id): id is ActionColumnId =>
      (ACTION_COLUMN_IDS as readonly string[]).includes(id)
    );
  } catch {
    return null;
  }
}
